import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Col,
  Row,
  Card,
  CardBody,
  Label,
  Input,
  Button,
  Spinner,
  FormFeedback,
} from "reactstrap"
import * as FileSaver from "file-saver"
import * as XLSX from "xlsx"
import { get } from "helpers/api_helper"
import moment from "moment"

const Step3Review = ({ pageView, validation, categoryOptions = [], surveyOptions = [], campaignId }) => {
  const [exportLoading, setExportLoading] = useState(false)

  const handleCopyToClipboard = (text) => {
    if (text) {
      navigator.clipboard.writeText(text)
    }
  }

  // Auto calculate total questions
  const totalQuestions = 
    (Number(validation.values.totalSurveyQuestions) || 0) + 
    (Number(validation.values.setFilterQuestion) || 0)

  // Handle export Excel
  const handleExportExcel = async () => {
    if (!campaignId || validation.values.status === "draft" || pageView !== "update") {
      return
    }

    try {
      setExportLoading(true)
      const result = await get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/admin/campaign/${campaignId}/export-links`
      )

      if (result && result.data && result.data.links && Array.isArray(result.data.links)) {
        // Format data for Excel
        const excelData = result.data.links.map(link => ({
          ID: link.id,
          UUID: link.uuid,
          URL: link.url,
          "Bucket ID": link.bucket_id,
          "Create Date": moment(link.create_date).format("YYYY-MM-DD HH:mm:ss"),
        }))

        // Create Excel file
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
        const fileExtension = ".xlsx"
        const fileName = `campaign_${campaignId}_export_${moment().format("YYYYMMDD_HHmmss")}`

        const ws = XLSX.utils.json_to_sheet(excelData)
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] }
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
        const data = new Blob([excelBuffer], { type: fileType })
        FileSaver.saveAs(data, fileName + fileExtension)
      } else {
        alert("No data available to export")
      }
    } catch (error) {
      console.error("Error exporting Excel:", error)
      alert("System error, Please try again.")
    } finally {
      setExportLoading(false)
    }
  }

  // Check if export button should be enabled
  const isExportEnabled = pageView === "update" && validation.values.status !== "draft" && campaignId

  return (
    <>
      {/* Block 1: Survey Information */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              Survey Information
            </div>
            <CardBody className="p-0">

              {/* Select Survey for Set Campaign */}
              <div className="mb-0">
                <div
                  className="py-2 px-3"
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#495057",
                    fontWeight: "600",
                  }}
                >
                  Select Survey for Set Campaign
                </div>

                <div className="px-3 pt-3">
                  {/* Row 1: Category & Survey Name */}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Label className="form-label">Category <span className="text-danger">*</span></Label>
                      <Input
                        name="categoryId"
                        type="select"
                        className="form-control"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.categoryId}
                        invalid={
                          validation.touched.categoryId && validation.errors.categoryId
                            ? true
                            : false
                        }
                        disabled={pageView === "view"}
                      >
                        <option value="">-- Select Category --</option>
                        {categoryOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.category_name}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.categoryId && validation.errors.categoryId ? (
                        <FormFeedback type="invalid">
                          {validation.errors.categoryId}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Survey Name <span className="text-danger">*</span></Label>
                      <Input
                        name="surveyId"
                        type="select"
                        className="form-control"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.surveyId}
                        invalid={
                          validation.touched.surveyId && validation.errors.surveyId
                            ? true
                            : false
                        }
                        disabled={pageView === "view" || !validation.values.categoryId}
                      >
                        <option value="">-- Select Survey Name --</option>
                        {surveyOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.survey_name}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.surveyId && validation.errors.surveyId ? (
                        <FormFeedback type="invalid">
                          {validation.errors.surveyId}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>

                  {/* Row 2: Total Survey Questions, Fix Questions, AI add-on Questions */}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Label className="form-label">Total Survey Questions</Label>
                      <Input
                        name="totalSurveyQuestions"
                        type="number"
                        className="form-control"
                        placeholder="0"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.totalSurveyQuestions}
                        disabled={true}
                      />
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Fix Questions</Label>
                      <Input
                        name="fixQuestions"
                        type="number"
                        className="form-control"
                        placeholder="0"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.fixQuestions}
                        disabled={true}
                      />
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">AI add-on Questions</Label>
                      <Input
                        name="aiAddonQuestions"
                        type="number"
                        className="form-control"
                        placeholder="0"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.aiAddonQuestions}
                        disabled={true}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <Label className="form-label">Set Filter Question <span className="text-danger">*</span></Label>
                      <Input
                        name="setFilterQuestion"
                        type="text"
                        className="form-control"
                        placeholder="the number of the filter question"
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '' || /^\d+$/.test(value)) {
                            validation.handleChange(e)
                          }
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.setFilterQuestion}
                        invalid={
                          validation.touched.setFilterQuestion && validation.errors.setFilterQuestion
                            ? true
                            : false
                        }
                        disabled={pageView === "view"}
                      />
                      {validation.touched.setFilterQuestion && validation.errors.setFilterQuestion ? (
                        <FormFeedback type="invalid">
                          {validation.errors.setFilterQuestion}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Total Questions (Auto Calculate)</Label>
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Auto Calculated"
                        value={totalQuestions}
                        disabled
                      />
                    </Col>
                  </Row>

                  {/* Row 4: Input all the filter questions */}
                  <Row className="mb-3">
                    <Col md={8}>
                      <Label className="form-label">Input all the filter questions</Label>
                      <Input
                        name="filterQuestionsText"
                        type="textarea"
                        className="form-control"
                        placeholder="(ex: Please tell me your gender. 1 Male; 2 Female..)"
                        rows="4"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.filterQuestionsText || ""}
                        invalid={
                          validation.touched.filterQuestionsText && validation.errors.filterQuestionsText
                            ? true
                            : false
                        }
                        disabled={pageView === "view"}
                      />
                      {validation.touched.filterQuestionsText && validation.errors.filterQuestionsText ? (
                        <FormFeedback type="invalid">
                          {validation.errors.filterQuestionsText}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>
                </div>
              </div>

              {/* URL */}
              <div className="mb-0">
                <div
                  className="py-2 px-3"
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#495057",
                    fontWeight: "600",
                  }}
                >
                  URL
                </div>

                <div className="px-3 pt-3">
                  {/* Row 1: Test URL */}
                  <Row className="mb-3 align-items-center">
                    <Label className="col-md-2 col-form-label">Test URL</Label>
                    <Col md={6}>
                      <div className="input-group">
                        <Input
                          name="testUrl"
                          type="text"
                          className="form-control"
                          placeholder="Enter Test URL"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.testUrl}
                          disabled={true}
                        />
                        <Button
                          type="button"
                          style={{ backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                          onClick={() => handleCopyToClipboard(validation.values.testUrl)}
                        >
                          <i className="bx bx-copy me-1"></i>
                          Copy
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* Row 2: Survey URL & Export Button */}
                  <Row className="mb-3 align-items-center">
                    <Label className="col-md-2 col-form-label">Survey URL</Label>
                    <Col md={6}>
                      <Button
                        type="button"
                        style={{ 
                          backgroundColor: isExportEnabled ? "#34c38f" : "#fff", 
                          borderColor: "#ced4da", 
                          color: isExportEnabled ? "#fff" : "#495057", 
                          minWidth: "120px" 
                        }}
                        disabled={!isExportEnabled}
                        onClick={handleExportExcel}
                      >
                        {exportLoading ? (
                          <>
                            <Spinner size="sm" className="me-1" color={isExportEnabled ? "light" : "secondary"} />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <i className="bx bx-export me-1"></i>
                            Export
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>

                  {/* Row 3: End Page URL */}
                  <Row className="mb-3 align-items-center">
                    <Label className="col-md-2 col-form-label">End Page URL</Label>
                    <Col md={6}>
                      <div className="input-group">
                        <Input
                          name="endPageUrl"
                          type="text"
                          className="form-control"
                          placeholder="Enter End Page URL"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.endPageUrl}
                          disabled={true}
                        />
                        <Button
                          type="button"
                          style={{ backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                          onClick={() => handleCopyToClipboard(validation.values.endPageUrl)}
                        >
                          <i className="bx bx-copy me-1"></i>
                          Copy
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>

              {/* Additional Rewards Condition - Hidden for now */}
              {/* <div className="mb-0 pb-3">
                <div
                  className="py-2 px-3"
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#495057",
                    fontWeight: "600",
                  }}
                >
                  Additional Rewards Condition
                </div>

                <div className="px-3 pt-3">
                  <Row className="mb-3 align-items-center">
                    <Col md={12}>
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <span>When user earn 40 Add on points then they will get</span>
                        <div style={{ position: "relative" }}>
                          <Input
                            name="addonPointsReward"
                            type="number"
                            className="form-control"
                            style={{ width: "100px" }}
                            placeholder="0"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.addonPointsReward}
                            invalid={
                              validation.touched.addonPointsReward && validation.errors.addonPointsReward
                                ? true
                                : false
                            }
                            disabled={pageView === "view"}
                          />
                          {validation.touched.addonPointsReward && validation.errors.addonPointsReward ? (
                            <FormFeedback type="invalid" style={{ position: "absolute", whiteSpace: "nowrap" }}>
                              {validation.errors.addonPointsReward}
                            </FormFeedback>
                          ) : null}
                        </div>
                        <span>RMB <span className="text-danger">*</span></span>
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <div className="d-flex align-items-center">
                        <div 
                          className="d-flex align-items-center"
                          style={{ cursor: pageView === "view" ? "not-allowed" : "pointer" }}
                          onClick={() => {
                            if (pageView !== "view") {
                              validation.setFieldValue("showDisclaimer", !validation.values.showDisclaimer)
                            }
                          }}
                        >
                          <div
                            style={{
                              width: "36px",
                              height: "20px",
                              backgroundColor: validation.values.showDisclaimer ? "#34c38f" : "#e9ecef",
                              borderRadius: "10px",
                              position: "relative",
                              transition: "background-color 0.3s",
                              cursor: pageView === "view" ? "not-allowed" : "pointer",
                            }}
                          >
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                position: "absolute",
                                top: "2px",
                                left: validation.values.showDisclaimer ? "18px" : "2px",
                                transition: "left 0.3s",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              }}
                            />
                          </div>
                          <Label className="mb-0 ms-2" style={{ cursor: pageView === "view" ? "not-allowed" : "pointer" }}>
                            Show Disclaimer Agreement
                          </Label>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div> */}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Block 2: Campaign Setting */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              Campaign Setting
            </div>
            <CardBody>

              {/* Row 1: Campaign Name */}
              <Row className="mb-3">
                <Col md={4}>
                  <Label htmlFor="campaignName" className="form-label">
                    Campaign Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="campaignName"
                    name="campaignName"
                    type="text"
                    className="form-control"
                    placeholder="Enter Campaign Name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.campaignName}
                    invalid={
                      validation.touched.campaignName && validation.errors.campaignName
                        ? true
                        : false
                    }
                    disabled={pageView === "view"}
                  />
                  {validation.touched.campaignName && validation.errors.campaignName ? (
                    <FormFeedback type="invalid">
                      {validation.errors.campaignName}
                    </FormFeedback>
                  ) : null}
                </Col>
              </Row>

              {/* Row 2: Start Date & End Date */}
              <Row className="mb-3">
                <Col md={4}>
                  <Label htmlFor="startDate" className="form-label">
                    Start Date: <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="startDate"
                    name="startDate"
                    type="date"
                    className="form-control"
                    min={moment().format("YYYY-MM-DD")}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.startDate}
                    invalid={
                      validation.touched.startDate && validation.errors.startDate
                        ? true
                        : false
                    }
                    disabled={pageView === "view"}
                  />
                  {validation.touched.startDate && validation.errors.startDate ? (
                    <FormFeedback type="invalid">
                      {validation.errors.startDate}
                    </FormFeedback>
                  ) : null}
                </Col>
                <Col md={4}>
                  <Label htmlFor="endDate" className="form-label">
                    End Date: <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="endDate"
                    name="endDate"
                    type="date"
                    className="form-control"
                    min={validation.values.startDate || moment().format("YYYY-MM-DD")}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.endDate}
                    invalid={
                      validation.touched.endDate && validation.errors.endDate
                        ? true
                        : false
                    }
                    disabled={pageView === "view"}
                  />
                  {validation.touched.endDate && validation.errors.endDate ? (
                    <FormFeedback type="invalid">
                      {validation.errors.endDate}
                    </FormFeedback>
                  ) : null}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

Step3Review.propTypes = {
  pageView: PropTypes.string,
  validation: PropTypes.object,
  categoryOptions: PropTypes.array,
  surveyOptions: PropTypes.array,
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default Step3Review
